/**
 * Public "Reported Concerns" map/list status pill (public-concerns-map.md / ADR-0023, Sue's mockup).
 * The THREE public statuses — NOT the full internal RequestStatus lifecycle (api/types.js's
 * PublicConcernStatus) — reuse the SAME color tokens StatusPill already draws on (the
 * acknowledged/inprogress/resolved status-fg/bg/dot vars: amber/violet/green, an exact match to the
 * approved mockup's open/inprog/resolved palette), but add a DISTINCT glyph shape per status
 * (diamond / half-circle / check) so status is never color-alone (WCAG 1.4.1) — color + glyph +
 * text label, all three, every time this renders (map legend, map popover, and the accessible
 * status table all reuse this one component so they can never drift).
 */
import type { PublicConcernStatus } from "../api/types.js";

const STATUS_TOKEN: Record<PublicConcernStatus, string> = {
  open: "acknowledged",
  "in-progress": "inprogress",
  resolved: "resolved",
};

export const PUBLIC_CONCERN_STATUS_LABEL: Record<PublicConcernStatus, string> = {
  open: "Open",
  "in-progress": "In progress",
  resolved: "Resolved",
};

/** A small, distinct SHAPE per status (not just a plain dot) — diamond (open/new), half-filled
 *  circle (in-progress), checkmark-in-circle (resolved). Purely decorative (the pill's text label
 *  is the accessible name) — aria-hidden. */
function StatusGlyph({ status }: { status: PublicConcernStatus }): JSX.Element {
  if (status === "open") {
    return (
      <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
        <path d="M6 0 L12 6 L6 12 L0 6 Z" fill="currentColor" />
      </svg>
    );
  }
  if (status === "in-progress") {
    return (
      <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
        <circle cx="6" cy="6" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M6 0.5 A5.5 5.5 0 0 1 6 11.5 Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
      <circle cx="6" cy="6" r="5.5" fill="currentColor" />
      <path
        d="M3.2 6.3 L5.1 8.2 L8.8 4.2"
        fill="none"
        stroke="var(--td-status-resolved-bg, #fff)"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ConcernStatusPill({
  status,
  size = "md",
}: {
  status: PublicConcernStatus;
  size?: "sm" | "md";
}): JSX.Element {
  const token = STATUS_TOKEN[status];
  return (
    <span
      className={`td-pill td-pill--${size} td-concern-status-pill`}
      style={{
        color: `var(--td-status-${token}-fg)`,
        background: `var(--td-status-${token}-bg)`,
      }}
    >
      <span className="td-concern-status-pill__glyph" style={{ color: `var(--td-status-${token}-dot)` }}>
        <StatusGlyph status={status} />
      </span>
      {PUBLIC_CONCERN_STATUS_LABEL[status]}
    </span>
  );
}
